import React, { type FC, useMemo, useRef, useState } from "react";
import { BusinessForm, type FormHandle } from "@src/components/business-form";
import { measureEndDecorator } from "@src/helpers/business/measureEndDecorator";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import FormFooter from "@shared/ui/form-footer";
import { FormPanel } from "@shared/ui/form-panel";
import { DetailList, DetailRow } from "@shared/ui/detail-row";
import { Spinner } from "@shared/ui/spinner";
import { useWsInvActContentDetailQuery } from "../../hooks/useWsInvActContentDetailQuery";
import type { SetFactQtyMutationVariables, UseWsInvActCardMutationsResult } from "../../useWsInvActCardMutations";
import { wsInvActContentFormConfig, type WsInvActContentFormValues } from "./wsInvActContent.form-config";
import "./WsInvActContentForm.scss";

/** Пропсы редактирования элемента состава АИ (form window). */
export interface WsInvActContentFormProps {
    contentCode: number;
    actNum: string;
    readonly: boolean;
    setFactQtyMutation: UseWsInvActCardMutationsResult["setFactQtyMutation"];
}

/**
 * Просмотр и редактирование фактического количества элемента состава АИ.
 */
export const WsInvActContentForm: FC<WsInvActContentFormProps> = ({ contentCode, readonly, setFactQtyMutation }) => {
    const formApiRef = useRef<FormHandle<WsInvActContentFormValues> | null>(null);
    const initialFactQtyRef = useRef<number | null>(null);
    const [addQty, setAddQty] = useState(0);

    const contentQuery = useWsInvActContentDetailQuery(contentCode);

    const contentData = contentQuery.data;
    const measure = contentData?.WsDepotCard?.Measure;

    const formDefaults = useMemo((): WsInvActContentFormValues | undefined => {
        if (contentData == null) {
            return undefined;
        }
        return {
            factQty: contentData.FactQty,
        };
    }, [contentData]);

    if (contentData != null && initialFactQtyRef.current == null) {
        initialFactQtyRef.current = contentData.FactQty;
    }

    const handleSubmit = async (values: WsInvActContentFormValues): Promise<void> => {
        if (initialFactQtyRef.current == null) {
            return;
        }

        const variables: SetFactQtyMutationVariables = {
            contentCode,
            initialFactQty: initialFactQtyRef.current,
            factQty: values.factQty,
        };

        const newFactQty = await setFactQtyMutation.mutateAsync(variables);
        initialFactQtyRef.current = newFactQty;
    };

    const isReady = !contentQuery.isFetching && formDefaults != null;
    const unitDecorator = measureEndDecorator(measure);

    return (
        <FormPanel<WsInvActContentFormValues>
            disabled={readonly}
            formApiRef={formApiRef}
            useDefaultFooter={false}
            loadingFallback={<Spinner />}
            formProps={{
                formConfig: wsInvActContentFormConfig,
                initialValues: formDefaults,
                ready: isReady,
                onSubmit: handleSubmit,
            }}
            footer={
                !readonly
                    ? ({ submitForm, isFormSubmitting }) => (
                          <FormFooter confirmText="Сохранить" confirmLoading={isFormSubmitting} onConfirm={submitForm} />
                      )
                    : undefined
            }
        >
            {(form) =>
                contentData == null ? null : (
                    <div className="ws_inv_act_content_form">
                        <DetailList>
                            <DetailRow label="№ карточки" value={contentData.WsDepotCard?.Num ?? ""} />
                            <DetailRow label="Обозначение" value={contentData.WsDepotCard?.Stockobj?.Sign ?? ""} />
                            <DetailRow label="Наименование" value={contentData.WsDepotCard?.Stockobj?.Name ?? ""} />
                            <DetailRow label="Заводской номер" value={contentData.WsDepotCard?.Stockobj?.NomNum ?? ""} />
                            <DetailRow
                                label="Тип продукции"
                                value={contentData.WsDepotCard?.ProductionType?.ShortName ?? ""}
                            />
                        </DetailList>
                        <div className="ws_inv_act_content_form__qty_block">
                            <BusinessForm.Field name="factQty">
                                <Input
                                    type="number"
                                    disabled={readonly}
                                    {...form.field("factQty").getNumberInputProps()}
                                    endDecorator={unitDecorator}
                                />
                            </BusinessForm.Field>
                            <BusinessForm.Row columns="1fr auto" align="end">
                                <Input
                                    type="number"
                                    disabled={readonly}
                                    label="Добавить кол-во факт"
                                    endDecorator={unitDecorator}
                                    value={addQty}
                                    onChange={(event) => {
                                        const parsed = Number(event.target.value);
                                        setAddQty(Number.isNaN(parsed) ? 0 : parsed);
                                    }}
                                />
                                <Button
                                    type="button"
                                    disabled={readonly}
                                    onClick={() => {
                                        const factQty = form.getValues("factQty");
                                        form.setValue("factQty", factQty + addQty, { shouldDirty: true });
                                        setAddQty(0);
                                    }}
                                >
                                    +
                                </Button>
                            </BusinessForm.Row>
                        </div>
                    </div>
                )
            }
        </FormPanel>
    );
};
